import { useEffect, useState, useCallback } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";

import {
  FaUtensils,
  FaHome,
  FaBus,
  FaShoppingBag,
  FaFilm,
  FaWallet,
} from "react-icons/fa";

import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import TransactionForm from "../../components/dashboard/TransactionForm";

import TransactionItem from "../../components/explorer/TransactionItem";
import SearchBar from "../../components/explorer/SearchBar";

import "./index.css";

const categories = [
  { name: "Food", icon: FaUtensils },
  { name: "Rent", icon: FaHome },
  { name: "Transport", icon: FaBus },
  { name: "Shopping", icon: FaShoppingBag },
  { name: "Entertainment", icon: FaFilm },
  { name: "Other", icon: FaWallet },
];

const PAGE_SIZE = 5;

const Dashboard = () => {
  const { user } = useAuth();

  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);

  const [lastDoc, setLastDoc] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // 🔹 SEARCH + FILTER STATE
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // 🔹 FETCH
  const fetchTransactions = useCallback(async () => {
    const q = query(
      collection(db, "transactions"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );

    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setTransactions(data);
  }, [user]);

  // 🔹 LOAD MORE
  const loadMore = async () => {
    if (!lastDoc) return;

    setLoadingMore(true);

    const q = query(
      collection(db, "transactions"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(PAGE_SIZE),
    );

    const snapshot = await getDocs(q);

    const newData = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    setTransactions((prev) => [...prev, ...newData]);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
    setLoadingMore(false);
  };

  useEffect(() => {
    if (user) {
      fetchTransactions();
    }
  }, [user, fetchTransactions]);

  // 🔹 DELETE
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await deleteDoc(doc(db, "transactions", id));
    fetchTransactions();
  };

  // 🔹 TOTAL
  const totalExpense = transactions.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0,
  );

  // 🔹 CATEGORY TOTALS
  const categoryTotals = categories.reduce((acc, cat) => {
    acc[cat.name] = transactions
      .filter((t) => t.category === cat.name)
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
    return acc;
  }, {});

  // 🔹 FILTERED LIST
  const filteredTransactions = transactions.filter((t) => {
    const matchesSearch = t.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "All" || t.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Welcome {user?.email}</h1>
        <p>Track your daily expenses</p>
      </div>

      <div className="total-card">
        <p>Total Expenses</p>
        <h2>- ₹{totalExpense.toFixed(2)}</h2>
      </div>

      <h3 className="section-title">Categories</h3>

      <div className="category-grid">
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.name} className="category-card">
              <div className="category-icon">
                <Icon size={26} />
              </div>
              <h4>{cat.name}</h4>
              <p>₹{categoryTotals[cat.name]?.toFixed(2)}</p>
            </div>
          );
        })}
      </div>

      {/* HEADER + SEARCH + ADD */}
      <div className="recent-heading-add-transaction-block">
        <h3 className="section-title">Recent Transactions</h3>

        <button
          className="add-btn"
          onClick={() => {
            setEditingTxn(null);
            setShowForm(true);
          }}
        >
          + Add Transaction
        </button>
      </div>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        categories={categories}
      />

      {/* LIST */}
      <div className="txn-list">
        {filteredTransactions.map((txn) => (
          <TransactionItem
            key={txn.id}
            txn={txn}
            categories={categories}
            onEdit={(txn) => {
              setEditingTxn(txn);
              setShowForm(true);
            }}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {lastDoc && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button className="add-btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {showForm && (
        <TransactionForm
          onClose={() => setShowForm(false)}
          onAdded={fetchTransactions}
          editData={editingTxn}
        />
      )}
    </div>
  );
};

export default Dashboard;
